import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { SbIcon } from './sidebarIcons';

export interface TourStop {
  key: string; tab: string; icon: string; line: string;
  src: string; alt: string;
}

/**
 * Tabs on one side, the real screen on the other.
 *
 * Each stop is one short sentence and the screenshot it is about, so a visitor
 * reads a dozen words and sees the product instead of reading a paragraph.
 * It steps on by itself every few seconds until the visitor chooses a tab, and
 * never for anyone who asked for reduced motion. The tab list is a proper ARIA
 * tablist, so arrow keys, Home and End work.
 */
export function ProductTour({ stops, interval = 6000 }: { stops: TourStop[]; interval?: number }) {
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(true);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!auto) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % stops.length), interval);
    return () => window.clearInterval(t);
  }, [auto, interval, stops.length]);

  // The active chip scrolls into view in the swipe row, without moving the page.
  useEffect(() => {
    const el = refs.current[i];
    if (el && el.parentElement) {
      const p = el.parentElement;
      p.scrollTo({ left: el.offsetLeft - (p.clientWidth - el.clientWidth) / 2, behavior: 'smooth' });
    }
  }, [i]);

  const pick = (n: number) => { setAuto(false); setI((n + stops.length) % stops.length); };
  const onKey = (e: KeyboardEvent) => {
    const k = e.key;
    const next = k === 'ArrowDown' || k === 'ArrowRight' ? i + 1
      : k === 'ArrowUp' || k === 'ArrowLeft' ? i - 1
      : k === 'Home' ? 0 : k === 'End' ? stops.length - 1 : null;
    if (next === null) return;
    e.preventDefault(); pick(next);
    window.requestAnimationFrame(() => refs.current[(next + stops.length) % stops.length]?.focus());
  };

  const cur = stops[i]!;
  return (
    <div className="tour">
      <div className="tour-tabs" role="tablist" aria-label="What NyumbaSuite does" onKeyDown={onKey}>
        {stops.map((s, n) => (
          <button key={s.key} ref={(el) => { refs.current[n] = el; }} type="button" role="tab"
            id={`tour-tab-${s.key}`} aria-selected={n === i} aria-controls="tour-panel"
            tabIndex={n === i ? 0 : -1}
            className={`tour-tab${n === i ? ' on' : ''}`} onClick={() => pick(n)}>
            <span className="tour-ico" aria-hidden="true"><SbIcon name={s.icon} size={20} /></span>
            <span className="tour-name">{s.tab}</span>
            <span className="tour-line">{s.line}</span>
          </button>
        ))}
      </div>

      <div className="tour-stage" id="tour-panel" role="tabpanel" aria-labelledby={`tour-tab-${cur.key}`}>
        <p className="tour-caption" aria-live="polite">{cur.line}</p>
        <div className="tour-frame">
          <div className="tour-bar" aria-hidden="true"><i /><i /><i /></div>
          <div className="tour-shots">
            {stops.map((s, n) => (
              <img key={s.key} src={s.src} alt={n === i ? s.alt : ''} width={1208} height={755}
                className={n === i ? 'on' : ''} loading={n === 0 ? 'eager' : 'lazy'} decoding="async"
                aria-hidden={n === i ? undefined : true} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
